<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  company: {
    type: Object,
    default: () => ({}),
  },
  isNewCompany: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const formData = ref({
  name: props.company?.name || '',
  domain: props.company?.domain || '',
  phone: props.company?.phone || '',
  website: props.company?.website || '',
  industry: props.company?.industry || '',
  description: props.company?.description || '',
});

const rules = computed(() => ({
  name: { required },
}));

const v$ = useVuelidate(rules, formData);

const isFormInvalid = computed(() => v$.value.$invalid);

const resetForm = () => {
  formData.value = {
    name: '',
    domain: '',
    phone: '',
    website: '',
    industry: '',
    description: '',
  };
  v$.value.$reset();
};

const resetValidation = () => {
  v$.value.$reset();
};

watch(
  formData,
  newValue => {
    if (!v$.value.$invalid) {
      emit('update', { ...newValue });
    }
  },
  { deep: true }
);

defineExpose({ isFormInvalid, resetForm, resetValidation });
</script>

<template>
  <div class="p-6 space-y-6">
    <div class="space-y-1">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{
          isNewCompany ? t('COMPANIES.CREATE.TITLE') : t('COMPANIES.EDIT.TITLE')
        }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('COMPANIES.CREATE.DESCRIPTION') }}
      </p>
    </div>

    <div class="space-y-4">
      <!-- Nome da Empresa (obrigatório) -->
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('COMPANIES.FORM.NAME.LABEL') }}
          <span class="text-n-ruby-9" aria-hidden="true">&#42;</span>
        </label>
        <Input
          v-model="formData.name"
          type="text"
          :placeholder="t('COMPANIES.FORM.NAME.PLACEHOLDER')"
          :has-error="v$.name.$error"
          @blur="v$.name.$touch"
        />
        <p v-if="v$.name.$error" class="mt-1 text-xs text-n-ruby-9">
          {{ t('COMPANIES.FORM.NAME.ERROR') }}
        </p>
      </div>

      <!-- Domínio e Setor (2 colunas) -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.DOMAIN.LABEL') }}
          </label>
          <Input
            v-model="formData.domain"
            type="text"
            :placeholder="t('COMPANIES.FORM.DOMAIN.PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.INDUSTRY.LABEL') }}
          </label>
          <Input
            v-model="formData.industry"
            type="text"
            :placeholder="t('COMPANIES.FORM.INDUSTRY.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Telefone e Website (2 colunas) -->
      <div class="grid grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.PHONE.LABEL') }}
          </label>
          <Input
            v-model="formData.phone"
            type="tel"
            :placeholder="t('COMPANIES.FORM.PHONE.PLACEHOLDER')"
          />
        </div>
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('COMPANIES.FORM.WEBSITE.LABEL') }}
          </label>
          <Input
            v-model="formData.website"
            type="url"
            :placeholder="t('COMPANIES.FORM.WEBSITE.PLACEHOLDER')"
          />
        </div>
      </div>

      <!-- Descrição -->
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('COMPANIES.FORM.DESCRIPTION.LABEL') }}
        </label>
        <TextArea
          v-model="formData.description"
          :placeholder="t('COMPANIES.FORM.DESCRIPTION.PLACEHOLDER')"
          :rows="3"
        />
      </div>
    </div>
  </div>
</template>
